import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';
import { opsDB } from '@/lib/data/ops';
import { ADMIN_NAV } from '@/lib/nav/adminNav';
import { navIconFor } from '@/ui/navIcons';
import { SkeletonTiles } from '@/ui/Loader';
import { PageHeader, StatTile } from '@/ui/primitives';
import { ChevronForwardIcon } from '@/icons';
import { PulseIcon, ShieldIcon } from '@/icons/nav';

export const Route = createFileRoute('/')({
  component: AdminDashboard,
});

interface WebhookStuck {
  id: string;
}
interface WebhooksResponse {
  stuck: WebhookStuck[];
  summary: Array<{ failed: number }>;
}

const AREA_BLURB: Record<string, string> = {
  '/approvals': 'Review new businesses, request changes, suspend.',
  '/bookings': 'Search any booking, refund with a reason.',
  '/webhooks': 'Stripe event log, stuck and failed deliveries.',
  '/hosts': 'Every live host, their listings and photos.',
  '/guests': 'Find a guest, anonymise on request.',
  '/content': 'Reviews and reports that need a decision.',
  '/finance': 'Export statements and ledgers for the accountant.',
  '/ops': 'Feature flags and platform switches.',
};

/** Founder console: the two queues that need a human today, then one tile
 * per admin area. Counts come from the same calls the areas themselves make
 * (`opsDB.listPending`, `admin-webhooks`), so a number here always matches
 * what the reader finds after clicking through. */
function AdminDashboard() {
  const { supabase } = useAuth();

  const pendingQuery = useQuery({
    queryKey: ['ops-pending', 'pending'],
    queryFn: async () => {
      const { data, error } = await opsDB.listPending(supabase);
      if (error) throw error;
      return data ?? [];
    },
  });
  const webhooksQuery = useQuery({
    queryKey: ['admin-webhooks', ''],
    queryFn: () => callEdgeFunction<WebhooksResponse>(supabase, 'admin-webhooks', { limit: 100 }),
  });

  const pending = pendingQuery.data ?? [];
  const needsReview = pending.filter((b) => b.reviewStatus === 'pending').length;
  const waitingOnOwner = pending.filter((b) => b.reviewStatus === 'rejected' || b.reviewStatus === 'info_requested').length;
  const suspended = pending.filter((b) => b.reviewStatus === 'suspended').length;
  const stuck = webhooksQuery.data?.stuck.length ?? 0;
  const failed = webhooksQuery.data?.summary.reduce((sum, r) => sum + r.failed, 0) ?? 0;

  const loading = pendingQuery.isLoading || webhooksQuery.isLoading;
  const areas = ADMIN_NAV.filter((item) => item.to !== '/');

  return (
    <section className="flex flex-col gap-xl">
      <PageHeader title="Founder console" eyebrow={format(new Date(), 'EEEE d MMMM')} lede="What needs a human today, and where everything else lives." />

      {loading ? (
        <SkeletonTiles />
      ) : (
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Businesses awaiting review"
            value={pendingQuery.isError ? '—' : needsReview}
            tone={needsReview > 0 ? 'attention' : 'good'}
            icon={<ShieldIcon />}
            hint={pendingQuery.isError ? "Couldn't load the queue" : needsReview === 0 ? 'All caught up' : undefined}
          />
          <StatTile label="Waiting on owner" value={pendingQuery.isError ? '—' : waitingOnOwner} hint="Changes or info requested" />
          <StatTile label="Suspended" value={pendingQuery.isError ? '—' : suspended} tone={suspended > 0 ? 'attention' : 'default'} />
          <StatTile
            label="Webhooks stuck or failed"
            value={webhooksQuery.isError ? '—' : stuck + failed}
            tone={stuck + failed > 0 ? 'attention' : 'good'}
            icon={<PulseIcon />}
            hint={webhooksQuery.isError ? "Couldn't reach the event log" : `${stuck} stuck · ${failed} failed`}
          />
        </div>
      )}

      <div>
        <h2 className="text-h3 font-bold text-ink">Areas</h2>
        <ul className="mt-md grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="group flex h-full items-start gap-md rounded-card border border-border bg-surface p-lg shadow-sm"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-tile bg-tint-sand text-canyon [&>svg]:h-5 [&>svg]:w-5">
                  {navIconFor(item.to)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-xs text-body font-bold text-ink">
                    {item.label}
                    <ChevronForwardIcon
                      className="h-3.5 w-3.5 text-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-canyon"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="mt-xs block text-small text-ink-2">{AREA_BLURB[item.to]}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
